// pages/HighlightBooksPage.tsx
// 抜粋・メモ（ハイライト）付き書籍の一覧ページ
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { fetchHighlightBooks } from '../utils/highlightsApi';
import { generateCoverUrlSmart } from '../utils/generateCoverUrl';
import { HighlightBookItem } from '../types/rlTypes';
import { Container, Grid, Card, CardMedia, Typography, Box, CircularProgress, Button } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useAppTokens } from '../context/ThemeModeContext';
import AddHighlightBookModal from '../components/AddHighlightBookModal';

const HighlightBooksPage: React.FC = () => {
  const [highlightBooks, setHighlightBooks] = useState<HighlightBookItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const { idToken } = useAuth();
  const navigate = useNavigate();

  // ===== Style tokens (GraphPage準拠) =====
  const TOKENS = useAppTokens();

  const glassPanelSx = {
    background: TOKENS.glass,
    border: `1px solid ${TOKENS.border}`,
    borderRadius: 2,
    boxShadow: '0 0 0 rgba(0,0,0,0)',
    backdropFilter: 'blur(6px)',
  } as const;

  const pillSx = {
    display: 'inline-flex',
    alignItems: 'center',
    gap: 1,
    px: 1.25,
    py: 0.75,
    borderRadius: 999,
    background: TOKENS.glassStrong,
    border: `1px solid ${TOKENS.border}`,
    color: TOKENS.text,
    fontSize: 12,
    lineHeight: 1,
    userSelect: 'none',
  } as const;

  const loadHighlightBooks = useCallback(async () => {
    if (!idToken) return;
    try {
      setLoading(true);
      const res = await fetchHighlightBooks(idToken);
      const items: HighlightBookItem[] = res.data?.items ?? [];
      setHighlightBooks(items);
    } catch (error) {
      console.error('Error fetching highlight books:', error);
      setError('抜粋・メモの取得に失敗しました。');
    } finally {
      setLoading(false);
    }
  }, [idToken]);

  useEffect(() => {
    loadHighlightBooks();
  }, [loadHighlightBooks]);

  const registeredBookIds = useMemo(
    () => new Set(highlightBooks.map((b) => b.rl_book_id)),
    [highlightBooks],
  );

  const getCoverImage = (book: HighlightBookItem): string => {
    return generateCoverUrlSmart(book.rl_book_id, book.title, book.isbn13);
  };

  const totalHighlights = highlightBooks.reduce((sum, b) => sum + (b.highlight_count ?? 0), 0);

  // Loading
  if (loading) {
    return (
      <Box
        sx={{
          width: '100vw',
          minHeight: '100dvh',
          background: `radial-gradient(1200px 600px at 20% 10%, ${TOKENS.radialGlow}, transparent 60%), ${TOKENS.bg}`,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          color: TOKENS.text,
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  // Error
  if (error) {
    return (
      <Box
        sx={{
          width: '100vw',
          minHeight: '100dvh',
          background: `radial-gradient(1200px 600px at 20% 10%, ${TOKENS.radialGlow}, transparent 60%), ${TOKENS.bg}`,
          color: TOKENS.text,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          px: 2,
        }}
      >
        <Box sx={{ ...glassPanelSx, p: 2 }}>
          <Typography sx={{ color: TOKENS.error, fontSize: 13 }}>{error}</Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box sx={{ mt: 7 }}>
      <Box
        sx={{
          width: '100%',
          minHeight: 'calc(100dvh - 56px)',
          background: `radial-gradient(1200px 600px at 20% 10%, ${TOKENS.radialGlow}, transparent 60%), ${TOKENS.bg}`,
          color: TOKENS.text,
          position: 'relative',
          overflowY: 'auto',
          WebkitOverflowScrolling: 'touch',
        }}
      >
        {/* page title */}
        <Box
          sx={{
            position: 'absolute',
            top: 14,
            left: 14,
            zIndex: 5,
            display: 'flex',
            gap: 1.5,
            alignItems: 'baseline',
            flexWrap: 'wrap',
          }}
        >
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: TOKENS.text }}>抜粋・メモ</Typography>
          <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>
            {highlightBooks.length}冊・{totalHighlights}件
          </Typography>
        </Box>

        <Container
          maxWidth={false}
          sx={{
            pt: 8,
            pb: 4,
            px: { xs: 2, md: 3 },
          }}
        >
          <Box
            sx={{
              ...glassPanelSx,
              height: { xs: 'auto', md: 'calc(100dvh - 96px)' },
              display: 'flex',
              flexDirection: 'column',
              overflow: { xs: 'visible', md: 'hidden' },
            }}
          >
            {/* header */}
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderBottom: `1px solid ${TOKENS.border}`,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                gap: 1,
                flexWrap: 'wrap',
              }}
            >
              <Typography sx={{ fontSize: 13, color: TOKENS.textSub }}>
                抜粋・メモのある本
              </Typography>
              <Button
                onClick={() => setAddModalOpen(true)}
                startIcon={<AddIcon sx={{ fontSize: 16 }} />}
                sx={{
                  ...pillSx,
                  textTransform: 'none',
                  cursor: 'pointer',
                  '&:hover': { background: TOKENS.glassStrong, borderColor: TOKENS.accent },
                }}
              >
                メモする本を追加
              </Button>
            </Box>

            {/* body */}
            <Box
              sx={{
                px: 2,
                py: 2,
                overflowY: { xs: 'visible', md: 'auto' },
                WebkitOverflowScrolling: 'touch',
                flex: { xs: 'unset', md: 1 },
                minHeight: 0,
              }}
            >
              {highlightBooks.length === 0 ? (
                <Typography sx={{ fontSize: 12.5, color: TOKENS.textSub }}>
                  抜粋・メモのある本がありません。
                </Typography>
              ) : (
                <Grid container spacing={1.5}>
                  {highlightBooks.map((book) => (
                    <Grid item xs={6} sm={4} md={3} key={book.rl_book_id}>
                      <Box>
                        <Card
                          onClick={() => {
                            navigate(`/highlights/${book.rl_book_id}`, { state: { book } });
                            window.scrollTo(0, 0);
                          }}
                          sx={{
                            position: 'relative',
                            transition: '0.25s',
                            background: TOKENS.cardBg,
                            border: `1px solid ${TOKENS.border}`,
                            borderRadius: 2,
                            overflow: 'hidden',
                            cursor: 'pointer',
                            boxShadow: 'none',
                            '&:hover': {
                              border: `1px solid ${TOKENS.accent}`,
                              background: TOKENS.cardBgHover,
                            },
                          }}
                        >
                          <CardMedia
                            component="img"
                            image={getCoverImage(book)}
                            alt={book.title ?? ''}
                            sx={{
                              objectFit: 'contain',
                              height: { xs: 170, sm: 200 },
                              width: '100%',
                              padding: '10px',
                              background: TOKENS.cardBg,
                            }}
                          />
                          {/* ハイライト件数バッジ */}
                          <Box
                            sx={{
                              ...pillSx,
                              position: 'absolute',
                              top: 8,
                              right: 8,
                              px: 1,
                              py: 0.5,
                              fontSize: 11,
                            }}
                          >
                            {book.highlight_count} 件
                          </Box>
                        </Card>

                        <Box sx={{ mt: 1, px: 0.25 }}>
                          <Typography
                            sx={{
                              fontSize: 12.5,
                              color: TOKENS.text,
                              lineHeight: 1.25,
                              display: '-webkit-box',
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: 2,
                              overflow: 'hidden',
                            }}
                          >
                            {book.title}
                          </Typography>

                          <Typography
                            sx={{
                              fontSize: 12,
                              color: TOKENS.textSub,
                              mt: 0.25,
                              display: '-webkit-box',
                              WebkitBoxOrient: 'vertical',
                              WebkitLineClamp: 1,
                              overflow: 'hidden',
                            }}
                          >
                            {book.author}
                          </Typography>

                          <Typography sx={{ fontSize: 12, color: TOKENS.textSub, mt: 0.25 }}>
                            {book.pub_year}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  ))}
                </Grid>
              )}
            </Box>
          </Box>
        </Container>
      </Box>

      <AddHighlightBookModal
        open={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        registeredBookIds={registeredBookIds}
        onBookAdded={() => loadHighlightBooks()}
      />
    </Box>
  );
};

export default HighlightBooksPage;
